import { ChevronDown } from 'lucide-react';
import * as React from 'react';

import { Button, type ButtonProps } from '@/ux-common/components/button/button';
import { cn } from '@/ux-common/lib/utils';

/**
 * DropdownButton — Minimal React wrapper for a button that opens a menu.
 *
 * Composes the Button atom with a chevron. All visual styling is delivered
 * through CSS classes applied via `className`:
 *
 * - Variant:  .btn-default, .btn-tier2, .btn-tier3, .btn-brand,
 *             .btn-destructive, .btn-link, .btn-inverse, .btn-transparent
 * - Size:     .btn-xs, .btn-sm, .btn-md, .btn-lg
 * - Shape:    .btn-rounded, .btn-round, .btn-circle, .btn-square
 *
 * The wrapper provides:
 * - Ref forwarding (via the underlying Button atom)
 * - `type="button"` default (inherited from Button)
 * - `data-component="DropdownButton"` for identification
 * - Default `aria-haspopup="menu"` (overridden by Radix / consumer if supplied)
 * - `aria-expanded` mirrored from `triggered` for non-Radix open-state control
 * - Conditional `<span>` wrapping the label for truncation support
 * - An optional leading icon: a `DropdownButtonIcon` child, rendered before the label
 * - A chevron that rotates 180deg when the button has either
 *   `[data-state="open"]` (Radix `asChild`) or `[aria-expanded="true"]`
 *   (manual `triggered`)
 *
 * Note: children is a required slot for text triggers. For icon-only usage
 * (square/circle shape with no children), supply `aria-label` for accessibility.
 *
 * @example
 * ```tsx
 * // Basic dropdown trigger
 * <DropdownButton className="btn-tier3">Sort By</DropdownButton>
 *
 * // With Radix DropdownMenuTrigger — auto state sync via data-state
 * <DropdownMenuTrigger asChild>
 *   <DropdownButton className="btn-tier3 btn-sm">Menu</DropdownButton>
 * </DropdownMenuTrigger>
 *
 * // Leading icon — a DropdownButtonIcon child; the rest of children is the label
 * <DropdownButton className="btn-tier3 w-64">
 *   <DropdownButtonIcon><SlidersHorizontal /></DropdownButtonIcon>
 *   {industries.join(', ')}
 * </DropdownButton>
 *
 * // Icon-only (requires aria-label)
 * <DropdownButton
 *   className="btn-tier2 btn-lg btn-square"
 *   aria-label="Open filters"
 *   triggered={isOpen}
 * />
 *
 * // Controlled triggered (non-Radix)
 * <DropdownButton className="btn-tier2" triggered={isOpen}>
 *   Advanced
 * </DropdownButton>
 * ```
 */

export interface DropdownButtonProps extends ButtonProps {
  /**
   * When defined, mirrors to `aria-expanded` on the rendered button. Use for
   * standalone open-state control when NOT composed inside a Radix trigger.
   * Leave undefined when used with `DropdownMenuTrigger asChild` / similar;
   * Radix sets `data-state="open"` automatically, which also rotates the chevron.
   */
  triggered?: boolean;
}

export type DropdownButtonIconProps = React.HTMLAttributes<HTMLSpanElement>;

/**
 * DropdownButtonIcon — the leading-icon slot. Pass it as a child of
 * `DropdownButton` with the icon inside; the rest of `children` is the label.
 *
 * DropdownButton lifts it out of `children` and renders it BEFORE the
 * `.dropdown-button-label` span, never inside: the label ellipsizes only its own
 * inline text, so an icon in there (or a wrapper around icon + text, such as an
 * `Inline`) wraps onto its own line or clips the label mid-word.
 *
 * It is a `.btn-icon`: the button's standard icon size (16px at xs/sm, 20px at md,
 * 24px at lg), never shrinking, its SVG filling the slot. It sets no colour, so it
 * takes the button's — the chevron's — and dims with it when disabled. The
 * button's flex gap spaces it from the label. Decorative: `aria-hidden`.
 *
 * Matched by `displayName` and looked for through fragments, the way Input's
 * trigger slots are (see `splitTriggerChildren`); one per button — a second one
 * replaces the first. `{cond && <DropdownButtonIcon …/>}` renders nothing when
 * `cond` is false.
 */
export const DropdownButtonIcon = React.forwardRef<HTMLSpanElement, DropdownButtonIconProps>(
  ({ className, ...props }, ref) => (
    <span
      ref={ref}
      aria-hidden="true"
      data-component="DropdownButtonIcon"
      className={cn('btn-icon dropdown-button-icon', className)}
      {...props}
    />
  )
);

DropdownButtonIcon.displayName = 'DropdownButtonIcon';

/**
 * Lifts a `DropdownButtonIcon` out of `children`, by `displayName` (a vendored copy
 * keeps its name, not its identity) and through fragments. The rest is the label.
 * Mirrors Input's `splitTriggerChildren` for one slot, kept local so DropdownButton
 * does not depend on Input.
 */
function splitIcon(children: React.ReactNode): {
  icon: React.ReactElement | null;
  label: React.ReactNode[];
} {
  let icon: React.ReactElement | null = null;
  const label: React.ReactNode[] = [];
  const visit = (nodes: React.ReactNode, keyPrefix: string) => {
    for (const child of React.Children.toArray(nodes)) {
      if (!React.isValidElement(child)) {
        label.push(child);
      } else if (child.type === React.Fragment) {
        visit((child.props as { children?: React.ReactNode }).children, `${keyPrefix}${child.key}`);
      } else if (
        (child.type as { displayName?: string } | null)?.displayName === 'DropdownButtonIcon'
      ) {
        icon = child;
      } else {
        label.push(
          keyPrefix ? React.cloneElement(child, { key: `${keyPrefix}${child.key}` }) : child
        );
      }
    }
  };
  visit(children, '');
  return { icon, label };
}

export const DropdownButton = React.forwardRef<HTMLButtonElement, DropdownButtonProps>(
  ({ children, className, triggered, ...props }, ref) => {
    const { icon, label } = splitIcon(children);
    return (
      <Button
        ref={ref}
        data-component="DropdownButton"
        aria-haspopup="menu"
        aria-expanded={triggered}
        className={cn('btn', className, 'dropdown-button')}
        {...props}
      >
        {icon}
        {label.length > 0 && <span className="dropdown-button-label">{label}</span>}
        <ChevronDown aria-hidden="true" className="glyph glyph-sm dropdown-button-chevron" />
      </Button>
    );
  }
);

DropdownButton.displayName = 'DropdownButton';
